import {IGroup, IGroupMessage} from 'constants/interfaces';
import {Avatar, Box, Typography, CircularProgress} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import Toolbar from '@material-ui/core/Toolbar';
import AppBar from '@material-ui/core/AppBar';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import {apiGetGroupMessages} from 'constants/Api';
import moment from 'moment';
import './GroupChat.scss';

export function GroupChat(props: {group?: IGroup; header?: boolean}) {
  const {group, header = true} = props;
  const [messages, setMessages] = useState<IGroupMessage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!group?.groupId) return;
    setIsLoading(true);
    setError('');
    apiGetGroupMessages(group.groupId)
      .then((res) => {
        setMessages(res);
      })
      .catch((err) => {
        setError('Failed to load messages');
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [group?.groupId]);

  // GroupChat is read-only: sending messages is not supported in this UI.

  return (
    <Box className="group-chat-container">
      {header && (
        <AppBar
          position="static"
          className="title-bar"
          elevation={0}
          variant="outlined"
        >
          <Toolbar>
            <Avatar src={group?.image || group?.avatar} className="avatar">
              {group?.name?.[0]?.toUpperCase()}
            </Avatar>
            <Box className="title">
              <Typography variant="body2" color="textPrimary">
                {group?.name || 'Group Chat'}
              </Typography>
              {group?.description && (
                <Typography variant="caption" color="textSecondary">
                  {group.description}
                </Typography>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Box
        className="order-chat-message-container"
        style={{overflowY: 'auto', flex: 1, padding: 16}}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : messages.length === 0 ? (
          <Box className="empty-state">
            <ChatBubbleOutlineIcon style={{fontSize: 48, marginBottom: 12}} />
            <Typography variant="body2" align="center">
              No messages yet
            </Typography>
            <Typography variant="caption" align="center" style={{marginTop: 4}}>
              No messages yet.
            </Typography>
          </Box>
        ) : (
          messages.map((msg) => (
            <Box key={msg.uuid} style={{marginBottom: 16}}>
              <Box display="flex" alignItems="center" style={{marginBottom: 4}}>
                <Avatar
                  src={msg.sender?.avatar}
                  style={{width: 24, height: 24, marginRight: 8}}
                >
                  {msg.sender?.username?.[0]?.toUpperCase()}
                </Avatar>
                <Typography variant="subtitle2" style={{marginRight: 8}}>
                  {msg.sender?.username}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {moment(msg.createdAt).format('HH:mm')}
                </Typography>
              </Box>
              <Typography variant="body2" style={{marginLeft: 32}}>
                {msg.content}
              </Typography>
            </Box>
          ))
        )}
      </Box>

      <Box
        className="response-bar"
        style={{padding: 12, borderTop: '1px solid rgba(0,0,0,0.08)'}}
      >
        <Typography variant="caption" color="textSecondary">
          Group messaging is disabled — this view is read-only.
        </Typography>
      </Box>
    </Box>
  );
}
